import type {
  DodajPominiete,
  OdpowiedzDodaniaPominietego,
  OdpowiedzZapisu,
  RecenzjaInfo,
  RecenzjaRozmowa,
  ZapisKorektyWskazania,
  ZapisPominietego,
} from "@shared/recenzjeTypy.js";
import { ApiError } from "./api.js";
import { nazwaPlikuZNaglowka } from "./naglowekPliku.js";

/**
 * Klient publicznego API recenzenta: /api/review/:token/*. Jedyna "autoryzacja" to token w adresie, wiec:
 * - token idzie w SCIEZCE (tak jak w adresie strony), nigdy w naglowku ani w query,
 * - zadania nie niosa ciasteczek (`credentials: "omit"`): administrator, ktory zagladnie na link w tej samej przegladarce,
 *   nie wysyla swojej sesji, a widok recenzenta nie moze jej przypadkiem uzyc.
 *
 * Kontrakt typow: shared/recenzjeTypy.ts. Ten modul wylacznie wola serwer; zadne decyzje o stanie nie zyja tutaj.
 *
 * Tabela sciezek i kodow odpowiedzi stoi w naglowku src/routes/reviewApi.ts (m.in. DELETE .../pominiete/:id
 * i GET .../word z plikiem Word).
 */

const BAZA = "/api/review";

async function bladZOdpowiedzi(res: Response): Promise<ApiError> {
  let code = "unknown";
  let szczegoly: Record<string, unknown> = {};
  try {
    const { error, ...reszta } = (await res.json()) as { error?: unknown } & Record<string, unknown>;
    if (typeof error === "string") code = error;
    szczegoly = reszta;
  } catch {
    // Odpowiedz bez JSON-a (np. 502 z nginx): zostaje sam status.
  }
  return new ApiError(res.status, code, szczegoly);
}

function adresLinku(token: string): string {
  return `${BAZA}/${encodeURIComponent(token)}`;
}

function adresRozmowy(token: string, sesjaId: string): string {
  return `${adresLinku(token)}/rozmowy/${encodeURIComponent(sesjaId)}`;
}

/**
 * Przegladarki wpuszczaja zadania `keepalive` (przezywaja zamkniecie karty i przejscie na inna strone) do lacznie 64 KiB
 * w locie. Zapis dluzszy niz ta granica z zapasem leci zwyklym zadaniem: `keepalive` przy zbyt duzym ciele konczy sie
 * odrzuceniem fetch, czyli utrata zapisu, a nie ocaleniem go.
 */
export const MAX_BAJTOW_KEEPALIVE = 60_000;

/** Czy zapis o takim ciele (JSON jako napis) wolno wyslac z `keepalive`. Liczymy bajty UTF-8, nie znaki. */
export function czyKeepalive(cialoJson: string): boolean {
  if (cialoJson.length > MAX_BAJTOW_KEEPALIVE) return false;
  const bajty = typeof TextEncoder === "undefined" ? cialoJson.length * 4 : new TextEncoder().encode(cialoJson).length;
  return bajty < MAX_BAJTOW_KEEPALIVE;
}

async function zadanie<T>(url: string, metoda: "GET" | "PUT" | "POST" | "DELETE", cialo?: unknown): Promise<T> {
  const json = cialo === undefined ? undefined : JSON.stringify(cialo);
  const res = await fetch(url, {
    method: metoda,
    credentials: "omit",
    headers: json === undefined ? undefined : { "Content-Type": "application/json" },
    body: json,
    // Zapis, ktory zdazyl wystartowac, ma dojsc takze wtedy, gdy recenzent zamyka karte albo wychodzi z rozmowy.
    ...(json !== undefined && czyKeepalive(json) ? { keepalive: true } : {}),
  });
  if (!res.ok) throw await bladZOdpowiedzi(res);
  return (await res.json()) as T;
}

export const recenzjaApi = {
  /** Lista rozmow pod linkiem z postepem. 404 `link_nieprawidlowy`: nieznany, wygasly albo uniewazniony token. */
  info: (token: string) => zadanie<RecenzjaInfo>(adresLinku(token), "GET"),

  /** Cala rozmowa: wypowiedzi, wskazania modelu z dotychczasowymi korektami, uwagi, notatka, postep. */
  rozmowa: (token: string, sesjaId: string) => zadanie<RecenzjaRozmowa>(adresRozmowy(token, sesjaId), "GET"),

  /** Werdykt, komentarz i wlasna wersja przy jednym wskazaniu. Same nulle = brak korekty. */
  zapiszWskazanie: (token: string, sesjaId: string, cialo: ZapisKorektyWskazania) =>
    zadanie<OdpowiedzZapisu>(`${adresRozmowy(token, sesjaId)}/wskazania`, "PUT", cialo),

  /**
   * Uwaga recenzenta przy wypowiedzi (w API "pominiete"): pierwszy zapis. Odpowiedz niesie id, ktore kolejne zapisy tej
   * uwagi wysylaja jako PUT (`zmienPominiete`), nigdy drugim POST-em.
   */
  dodajPominiete: (token: string, sesjaId: string, cialo: DodajPominiete) =>
    zadanie<OdpowiedzDodaniaPominietego>(`${adresRozmowy(token, sesjaId)}/pominiete`, "POST", cialo),

  /** Zmiana tresci i parametru zapisanej uwagi. Numer wypowiedzi sie nie zmienia. 404, gdy uwagi o tym id nie ma. */
  zmienPominiete: (token: string, sesjaId: string, pominieteId: string, cialo: ZapisPominietego) =>
    zadanie<OdpowiedzDodaniaPominietego>(
      `${adresRozmowy(token, sesjaId)}/pominiete/${encodeURIComponent(pominieteId)}`,
      "PUT",
      cialo,
    ),

  usunPominiete: (token: string, sesjaId: string, pominieteId: string) =>
    zadanie<OdpowiedzZapisu>(
      `${adresRozmowy(token, sesjaId)}/pominiete/${encodeURIComponent(pominieteId)}`,
      "DELETE",
    ),

  /** Notatka "know-how" do rozmowy (wolny tekst; pusty tekst ja czysci). */
  zapiszNotatke: (token: string, sesjaId: string, tekst: string) =>
    zadanie<OdpowiedzZapisu>(`${adresRozmowy(token, sesjaId)}/notatka`, "PUT", { tekst }),

  /** "Zakoncz przeglad tej rozmowy" i cofniecie tego oznaczenia. */
  zapiszPrzejrzana: (token: string, sesjaId: string, przejrzana: boolean) =>
    zadanie<OdpowiedzZapisu>(`${adresRozmowy(token, sesjaId)}/przejrzana`, "PUT", { przejrzana }),

  /**
   * Plik Word z raportem rozmowy. Pobieranie przez fetch (a nie zwykly link), zeby przycisk mial stan ladowania, a odmowy
   * serwera wrocily jako zdania. Zadanie bez ciasteczek, jak reszta.
   */
  pobierzWord: async (token: string, sesjaId: string): Promise<{ blob: Blob; nazwaPliku: string }> => {
    const res = await fetch(`${adresRozmowy(token, sesjaId)}/word`, { credentials: "omit" });
    if (!res.ok) throw await bladZOdpowiedzi(res);
    const blob = await res.blob();
    return { blob, nazwaPliku: nazwaPlikuZNaglowka(res.headers.get("Content-Disposition"), "docx") };
  },
};
